import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import { ApiProvider, createApiClientForBrowser } from "./api";
import { AuthProvider } from "./auth";
import "./styles.css";
import "./plan-skin.css";
import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't retry 4xx — a missing canvas / thread / attachment will
      // keep returning 404, and burning three retries per query × five
      // queries per page produced a "crash loop" feel (constant
      // spinner → error → spinner cycle) when a user landed on a URL
      // whose resource had been deleted. 5xx still gets one retry so
      // transient API hiccups recover.
      retry: (failureCount, error) => {
        const status =
          error && typeof error === "object" && "status" in error
            ? (error as { status?: number }).status
            : undefined;
        if (typeof status === "number" && status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

const apiClient = createApiClientForBrowser();

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: { auth: undefined as never, queryClient, apiClient },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById("app");
if (!rootEl) throw new Error("#app element not found");

createRoot(rootEl).render(
  <StrictMode>
    <ApiProvider client={apiClient}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider delay={250}>
            <RouterProvider router={router} />
            <Toaster closeButton position="top-center" />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ApiProvider>
  </StrictMode>,
);
